import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
    Box,
    Typography,
    Grid,
    useTheme,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogActions,
    Link,
    Select,
    MenuItem,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import IconButton from '@mui/material/IconButton';
import { Plus, Delete } from '../../assets/images';
import DidimHwDialog from '../../components/didimHwDialog';
import MapDialog from '../../components/mapDialog';
import OrganizationTypeEditDialog from '../../components/organizationTypeEditDialog';

import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import CommonCard from '../../components/card/CommonCard';
import Button from '../../components/button';
import StatusIndicator from '../../components/statusIndicator';
import { ORGANIZATION_TYPES, ORGANIZATION_STATE, getRegionOptions } from '../../constants';
import { organizationAPI, userAPI, didimAPI, membershipAPI } from '../../actions/imsServer';
import { formatDateTimeDisplay, isSupervisor, isAdmin, toDisplayString } from '../../utils/base';
import Popup from '../../components/popup';
import Toast from '../../components/toast';
import { Memo } from '../../assets/images';
import InputField from '../../components/formItem/inputField';

const ApplicationDetail = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const [originOrganization] = useState(location.state?.organization || location.state?.organizationData);
    const [organization, setOrganization] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [adminUser, setAdminUser] = useState(null);
    const [info, setInfo] = useState('');
    const [stateMemo, setStateMemo] = useState('');

    const [connectedDidims, setConnectedDidims] = useState([]);
    const [stateInfoDate, setStateInfoDate] = useState(null);
    const [stateInfoUserName, setStateInfoUserName] = useState(null);

    // 디딤H/W 선택 팝업 상태
    const [didimHwDialogOpen, setDidimHwDialogOpen] = useState(false);
    const [removedDidimHwList, setRemovedDidimHwList] = useState([]); // 해제된 디딤H/W 목록

    // 편집 모드 상태
    const [isEditMode, setIsEditMode] = useState(false);

    // 편집 가능한 필드들
    const [editData, setEditData] = useState({
        name: '',
        type: '',
        password: '',
        confirmPassword: '',
        userName: '',
        contactInfo: '',
        phone: '',
        email: '',
        country: 'ko',
        zipcode: '',
        address: '',
        addressDetail: '',
        latitude: '',
        longitude: '',
        region: '',
        divisionSections: null,
        info: '',
        file: null,
        filePreview: null
    });

    // 기관유형 보기 팝업 상태
    const [typeViewDialogOpen, setTypeViewDialogOpen] = useState(false);

    // 기관유형 편집 팝업 상태
    const [editDialogOpen, setEditDialogOpen] = useState(false);

    // 지도 팝업 상태 (경도 보기)
    const [mapDialogOpen, setMapDialogOpen] = useState(false);
    const [mapPosition, setMapPosition] = useState([37.5665, 126.9780]);

    // 팝업 상태
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [popupSubMessage, setPopupSubMessage] = useState('');

    // 확인 팝업 상태
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmMessage, setConfirmMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(null); // 'delete', 'reject', 'approve'

    // 토스트 상태
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastSeverity, setToastSeverity] = useState('warning'); // 'success', 'error', 'warning', 'info'

    // meInfo 가져오기
    const [meInfo, setMeInfo] = useState(null);
    useEffect(() => {
        const meInfoString = localStorage.getItem('meInfo');
        if (meInfoString) {
            setMeInfo(JSON.parse(meInfoString));
        }
    }, []);

    // 데이터 로드
    useEffect(() => {
        if (originOrganization) {
            fetchOrganizationDetail();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchOrganizationDetail는 의존성에서 제외
    }, [originOrganization?.id]);

    // organization이 변경될 때 stateInfo 초기화 및 editData 초기화
    useEffect(() => {
        if (organization) {
            // stateInfo가 JSON 형식인지 확인하고 파싱
            let stateMemo = '';
            let stateDate = null;
            let stateUserId = null;

            if (organization.stateInfo) {
                try {
                    const parsed = JSON.parse(organization.stateInfo);
                    // JSON 객체인 경우
                    if (typeof parsed === 'object') {
                        stateMemo = parsed.memo || '';
                        stateDate = parsed.date || null;
                        stateUserId = parsed.userId || null;
                    } else {
                        // JSON이 아니면 그대로 사용
                        stateMemo = organization.stateInfo;
                    }
                } catch (e) {
                    // JSON 파싱 실패 시 문자열로 사용
                    stateMemo = organization.stateInfo;
                }
            }
            setStateMemo(stateMemo);
            setStateInfoDate(stateDate);
            setInfo(organization.info || '');

            // userId로 사용자 정보 가져오기
            if (stateUserId) {
                userAPI.getUsers(stateUserId, { state: 0 })
                    .then(res => {
                        const userData = res?.data?.user || res?.user || null;
                        if (userData?.name) {
                            setStateInfoUserName(userData.name);
                        } else {
                            setStateInfoUserName(null);
                        }
                    })
                    .catch(err => {
                        console.error('Failed to fetch state info user:', err);
                        setStateInfoUserName(null);
                    });
            } else {
                setStateInfoUserName(null);
            }

            // editData 초기화
            setEditData({
                name: organization.name || '',
                type: organization.type !== undefined ? organization.type : '',
                password: '',
                confirmPassword: '',
                userName: adminUser?.name || '',
                contactInfo: organization.contactInfo || '',
                phone: organization.phone || adminUser?.phone || '',
                email: organization.email || adminUser?.email || '',
                country: organization.country || 'ko',
                zipcode: organization.zipcode || '',
                address: organization.address || '',
                addressDetail: organization.addressDetail || '',
                latitude: organization.latitude || '',
                longitude: organization.longitude || '',
                region: organization.region || '',
                divisionSections: organization.divisionSections || null,
                info: organization.info || '',
                file: null,
                filePreview: organization.file || null
            });

            // 연결된 디딤 H/W 초기화
            if (organization.didims && Array.isArray(organization.didims)) {
                const transformedDidims = organization.didims.map(item => ({
                    id: item.id,
                    organizationName: item?.organization?.name || '-',
                    name: item.name || `${t('didim')}${item.id}`,
                    info: item.info || '',
                    uid: item.uid || '',
                    modelName: item.modelName || 'Didim',
                    organizationId: item?.organizationId || 0,
                }));
                setConnectedDidims(transformedDidims);
                setRemovedDidimHwList([]); // 초기화 시 해제 목록도 초기화
            } else {
                setConnectedDidims([]);
                setRemovedDidimHwList([]);
            }
        }
    }, [organization, adminUser, t]);

    const fetchOrganizationDetail = async () => {
        try {
            setLoading(true);
            setError(null);

            if (!originOrganization) {
                setError(t('organization_information_not_found'));
                setLoading(false);
                return;
            }

            // 기관 상세 정보 가져오기 (expand로 didims, user 포함)
            try {
                const orgDetailRes = await organizationAPI.getOrganizations({
                    id: originOrganization.id,
                    expand: 'didims,user'
                });
                const orgDetail = orgDetailRes?.data?.organizations?.[0] || orgDetailRes?.organizations?.[0] || originOrganization;
                setOrganization(orgDetail);
            } catch (err) {
                console.error('Failed to fetch organization detail:', err);
                setOrganization(originOrganization);
            }

            // 관리자 정보 조회 (userId로)
            if (originOrganization.userId) {
                try {
                    const adminRes = await userAPI.getUsers(originOrganization.userId, { state: 0 });
                    const adminData = adminRes?.data?.user || adminRes?.user || null;
                    setAdminUser(adminData);
                } catch (err) {
                    console.error('Failed to fetch admin user:', err);
                }
            }
        } catch (err) {
            console.error('Failed to fetch organization detail:', err);
            setError(t('failed_to_load_organization_information'));
        } finally {
            setLoading(false);
        }
    };

    // 상태 정보 가져오기
    const getStatusInfo = (state) => {
        const stateNum = Number(state);
        const WITHDRAW_STATE = ORGANIZATION_STATE.find(s => s.value === 1);
        const PENDING_STATE = ORGANIZATION_STATE.find(s => s.value === 2);
        const RE_PENDING_STATE = ORGANIZATION_STATE.find(s => s.value === 3);
        const REJECTED_STATE = ORGANIZATION_STATE.find(s => s.value === 4);
        const DELETED_STATE = ORGANIZATION_STATE.find(s => s.value === 5);

        if (stateNum === PENDING_STATE?.value) {
            return { text: t(PENDING_STATE.label), color: theme.palette.figma.steps };
        } else if (stateNum === RE_PENDING_STATE?.value) {
            return { text: t(RE_PENDING_STATE.label), color: theme.palette.figma.yellowText };
        } else if (stateNum === REJECTED_STATE?.value) {
            return { text: t(REJECTED_STATE.label), color: theme.palette.figma.Red_didim };
        } else if (stateNum === DELETED_STATE?.value) {
            return { text: t(DELETED_STATE.label), color: theme.palette.figma.white[30] };
        } else if (stateNum === WITHDRAW_STATE?.value) {
            return { text: t(WITHDRAW_STATE.label), color: theme.palette.figma.white[30] };
        } else {
            return { text: t(PENDING_STATE?.label || 'pending_approval'), color: theme.palette.figma.steps };
        }
    };

    // 기관유형 보기 팝업 열기
    const handleViewOrganizationType = () => {
        if (isEditMode) {
            setEditDialogOpen(true);
        } else {
            setTypeViewDialogOpen(true);
        }
    };

    // 기관유형 보기 팝업 닫기
    const handleCloseTypeViewDialog = () => {
        setTypeViewDialogOpen(false);
    };

    // 기관유형 편집 팝업 열기 (reserved for future use)
    const _handleOpenEditDialog = () => setEditDialogOpen(true);
    void _handleOpenEditDialog;

    // 기관유형 편집 팝업 닫기
    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
    };

    // 기관유형 편집 팝업 저장 핸들러
    const handleSaveOrganizationType = (data) => {
        handleEditDataChange('type', data.type);
        handleEditDataChange('divisionSections', data.divisionSections);
        setEditDialogOpen(false);
    };

    // 지도 팝업 열기 (위경도 보기)
    const handleViewMap = () => {
        if (isEditMode) {
            // 편집 모드일 때는 editData에서 가져오기
            if (editData.latitude && editData.longitude) {
                const lat = parseFloat(editData.latitude);
                const lng = parseFloat(editData.longitude);
                if (!isNaN(lat) && !isNaN(lng)) {
                    setMapPosition([lat, lng]);
                }
            }
        } else {
            // 일반 모드일 때는 organization에서 가져오기
            if (organization?.latitude && organization?.longitude) {
                setMapPosition([parseFloat(organization.latitude), parseFloat(organization.longitude)]);
            }
        }
        setMapDialogOpen(true);
    };

    // 지도 팝업 닫기
    const handleCloseMapDialog = () => {
        setMapDialogOpen(false);
    };

    // 지도 위치 저장 핸들러
    const handleMapPositionSave = (position) => {
        // 편집 모드일 때 editData도 업데이트 (원본 자릿수 유지)
        if (isEditMode) {
            handleEditDataChange('latitude', position[0].toString());
            handleEditDataChange('longitude', position[1].toString());
        }
        setMapDialogOpen(false);
    };

    // Memo 버튼 클릭 핸들러 (편집 모드 토글)
    const handleMemoClick = () => {
        setIsEditMode(!isEditMode);
    };

    // 디딤H/W 선택 팝업 핸들러
    const handleOpenDidimHwDialog = () => {
        setDidimHwDialogOpen(true);
    };

    const handleCloseDidimHwDialog = () => {
        setDidimHwDialogOpen(false);
    };

    const handleSelectDidimHw = (didimHw) => {
        // 이미 선택된 디딤H/W인지 확인
        const isAlreadySelected = connectedDidims.some(item => item.id === didimHw.id);

        if (!isAlreadySelected) {
            setConnectedDidims(prev => [...prev, didimHw]);
        }

        setDidimHwDialogOpen(false);
    };

    // 디딤H/W 제거 핸들러
    const handleRemoveDidimHw = (didimHwId) => {
        // 제거할 디딤 하드웨어 정보 찾기
        const removedItem = connectedDidims.find(item => item.id === didimHwId);

        if (removedItem) {
            // 해제된 목록에 추가
            setRemovedDidimHwList(prev => [...prev, removedItem]);
        }

        // 선택된 목록에서 제거
        setConnectedDidims(prev => prev.filter(item => item.id !== didimHwId));
    };


    // editData 필드 변경 핸들러
    const handleEditDataChange = (field, value) => {
        setEditData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // 파일 업로드 핸들러
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setEditData(prev => ({
                ...prev,
                file: file,
                filePreview: URL.createObjectURL(file)
            }));
        }
    };

    // 편집 취소 핸들러
    const handleCancelEdit = () => {
        // 원래 데이터로 되돌리기
        if (organization) {
            setEditData({
                name: organization.name || '',
                type: organization.type !== undefined ? organization.type : '',
                password: '',
                confirmPassword: '',
                managerName: adminUser?.name || '',
                contactInfo: organization.contactInfo || '',
                phone: organization.phone || adminUser?.phone || '',
                email: organization.email || adminUser?.email || '',
                country: organization.country || 'ko',
                zipcode: organization.zipcode || '',
                address: organization.address || '',
                addressDetail: organization.addressDetail || '',
                latitude: organization.latitude || '',
                longitude: organization.longitude || '',
                region: organization.region || '',
                divisionSections: organization.divisionSections || null,
                info: organization.info || '',
                file: null,
                filePreview: organization.file || null
            });
            setInfo(organization.info || '');
        }
        setIsEditMode(false);
    };

    // 편집 저장 핸들러
    const handleSaveEdit = async () => {
        if (!organization) return;

        try {
            // 1. 사용자 정보 업데이트 (담당자명, 연락처, 메일, 비밀번호)
            if (adminUser) {
                // 담당자명, 연락처, 메일이 변경된 경우 사용자 정보 업데이트
                const isUserInfoChanged =
                    (adminUser.name !== editData.userName) ||
                    (adminUser.mobile !== editData.phone) ||
                    (adminUser.email !== editData.email);

                if (isUserInfoChanged) {
                    await userAPI.updateUser({
                        id: adminUser.id,
                        name: editData.userName,
                        email: editData.email,
                        mobile: editData.phone,
                    });
                }

                // 비밀번호가 입력된 경우에만 비밀번호도 업데이트
                if (editData.password && editData.password.trim() !== '') {
                    await userAPI.resetPassword({ loginId: adminUser.loginId, newPassword: editData.password });
                }
            }

            // 2. 기관 정보 업데이트
            const divisionSections = editData.divisionSections
                ? (typeof editData.divisionSections === 'string'
                    ? editData.divisionSections
                    : JSON.stringify(editData.divisionSections))
                : organization.divisionSections;

            const updateData = {
                id: organization.id,
                name: editData.name,
                type: editData.type,
                contactInfo: editData.contactInfo,
                phone: editData.phone,
                email: editData.email,
                country: editData.country,
                zipcode: editData.zipcode,
                address: editData.address,
                addressDetail: editData.addressDetail,
                latitude: editData.latitude,
                longitude: editData.longitude,
                region: editData.region,
                divisionSections: divisionSections
            };

            // 파일이 선택된 경우 추가
            if (editData.file instanceof File) {
                updateData.file = editData.file;
            }

            // 어드민인 경우 state: 3 (재승인대기)으로 설정
            if (isAdmin(meInfo?.grade)) {
                updateData.state = 3;
                updateData.info = info;
            }

            await organizationAPI.updateOrganization(updateData);

            // 3. 디딤 하드웨어 연결 관리
            // 3-1. 새로 선택된 디딤 하드웨어 연결
            // 원래 연결되어 있던 디딤 H/W ID 목록
            const originalDidimIds = organization.didims?.map(d => d.id) || [];
            // 새로 추가된 디딤 H/W (현재 목록에 있지만 원래 목록에 없던 것)
            const newDidimHws = connectedDidims.filter(d => !originalDidimIds.includes(d.id));

            if (newDidimHws.length > 0) {
                for (const didimHw of newDidimHws) {
                    try {
                        await didimAPI.updateDidim({
                            id: didimHw.id,
                            organizationId: organization.id
                        });
                    } catch (error) {
                        console.error(`디딤 ${didimHw.id} 기관 등록 실패:`, error);
                    }
                }
            }

            // 3-2. 해제된 디딤 하드웨어 연결 해제
            if (removedDidimHwList.length > 0) {
                for (const didimHw of removedDidimHwList) {
                    try {
                        await didimAPI.updateDidim({
                            id: didimHw.id,
                            organizationId: 0 // 0으로 설정하여 연결 해제
                        });
                    } catch (error) {
                        console.error(`디딤 ${didimHw.id} 기관 연결 해제 실패:`, error);
                    }
                }
            }

            showPopup(t('organization_information_has_been_updated'));
            setIsEditMode(false);

            await fetchOrganizationDetail();
        } catch (err) {
            console.error('Failed to update organization:', err);
            showPopup(t('failed_to_update_organization_information'), t('please_try_again'));
        }
    };

    // 팝업 표시 함수
    const showPopup = (message, subMessage = '') => {
        setPopupMessage(message);
        setPopupSubMessage(subMessage);
        setPopupOpen(true);
    };

    // 팝업 닫기 함수
    const handleClosePopup = () => {
        setPopupOpen(false);
    };

    // 토스트 표시 함수
    const showToast = (message, severity = 'warning') => {
        setToastMessage(message);
        setToastSeverity(severity);
        setToastOpen(true);
    };

    // 토스트 닫기 함수
    const handleCloseToast = () => {
        setToastOpen(false);
    };

    // stateInfo를 JSON 형식으로 생성하는 함수
    const createStateInfoJson = (memo) => {
        // 현재 사용자 정보 가져오기
        const meInfoString = localStorage.getItem('meInfo');
        const meInfo = meInfoString ? JSON.parse(meInfoString) : null;
        const userId = meInfo?.id || null;

        // 현재 시간 (ISO 형식)
        const date = new Date().toISOString();

        // JSON 객체 생성
        const stateInfoData = {
            date: date,
            userId: userId,
            memo: memo || ''
        };

        return JSON.stringify(stateInfoData);
    };

    // 확인 팝업에서 실제 실행할 함수
    const executeAction = async (action) => {
        if (!organization) return;

        try {
            const NORMAL = ORGANIZATION_STATE.find(s => s.value === 0)?.value; // 정상
            const PENDING = ORGANIZATION_STATE.find(s => s.value === 2)?.value; // 승인대기
            const REJECTED = ORGANIZATION_STATE.find(s => s.value === 4)?.value; // 반려
            const DELETED = ORGANIZATION_STATE.find(s => s.value === 5)?.value; // 삭제

            if (action === 'delete') {
                const stateInfoJson = createStateInfoJson(stateMemo);
                await organizationAPI.updateOrganization({
                    id: organization.id,
                    state: DELETED,
                    stateInfo: stateInfoJson
                });
                showPopup(t('organization_has_been_deleted'));
                setTimeout(() => {
                    navigate(-1);
                }, 1500);
            } else if (action === 'reject') {
                const stateInfoJson = createStateInfoJson(stateMemo);
                await organizationAPI.updateOrganization({
                    id: organization.id,
                    state: REJECTED,
                    stateInfo: stateInfoJson
                });
                showPopup(t('organization_has_been_rejected'), t('email_has_been_sent'));
                setTimeout(() => {
                    navigate(-1);
                }, 1500);
            } else if (action === 'approve') {
                const stateInfoJson = createStateInfoJson(''); // 승인 시 메모 없음
                await organizationAPI.updateOrganization({
                    id: organization.id,
                    state: NORMAL,
                    stateInfo: stateInfoJson
                });
                await membershipAPI.registerMembership({
                    userId: organization.userId,
                    organizationId: organization.id,
                    type: 0,
                    state: 0
                });
                showPopup(t('organization_has_been_approved'), t('email_has_been_sent'));
                setTimeout(() => {
                    navigate(-1);
                }, 1500);
            } else if (action === 'restore') {
                const stateInfoJson = createStateInfoJson(''); // 승인대기로 되돌리기 시 메모 없음
                await organizationAPI.updateOrganization({
                    id: organization.id,
                    state: PENDING,
                    stateInfo: stateInfoJson
                });
                showPopup(t('reverted_to_pending_approval'));
                setTimeout(() => {
                    navigate(-1);
                }, 1500);
            }
        } catch (err) {
            console.error(`Failed to ${action} organization:`, err);
            const errorMessages = {
                delete: t('delete_failed'),
                reject: t('reject_failed'),
                approve: t('approve_failed'),
                restore: t('restore_failed')
            };
            showPopup(errorMessages[action], t('please_try_again'));
        }
    };

    // 확인 팝업 확인 버튼 클릭
    const handleConfirmAction = () => {
        setConfirmDialogOpen(false);
        if (confirmAction) {
            executeAction(confirmAction);
        }
        setConfirmAction(null);
    };

    // 확인 팝업 취소 버튼 클릭
    const handleCancelAction = () => {
        setConfirmDialogOpen(false);
        setConfirmAction(null);
    };

    // 삭제
    const handleDelete = () => {
        if (!organization) return;

        // 메모 확인
        if (!stateMemo || stateMemo.trim() === '') {
            showToast(t('please_leave_a_reason_memo'), 'warning');
            return;
        }

        setConfirmMessage(t('do_you_want_to_delete_from_pending'));
        setConfirmAction('delete');
        setConfirmDialogOpen(true);
    };

    // 반려
    const handleReject = () => {
        if (!organization) return;

        // 메모 확인
        if (!stateMemo || stateMemo.trim() === '') {
            showToast(t('please_leave_a_reason_memo'), 'warning');
            return;
        }

        setConfirmMessage(t('do_you_want_to_reject_and_send_email'));
        setConfirmAction('reject');
        setConfirmDialogOpen(true);
    };

    // 승인
    const handleApprove = () => {
        if (!organization) return;

        setConfirmMessage(t('do_you_want_to_approve_and_send_email'));
        setConfirmAction('approve');
        setConfirmDialogOpen(true);
    };

    // 승인대기로 되돌리기
    const handleRestore = () => {
        if (!organization) return;

        setConfirmMessage(t('do_you_want_to_revert_to_pending_approval'));
        setConfirmAction('restore');
        setConfirmDialogOpen(true);
    };

    // 첨부서류 다운로드 URL 생성
    const getFileUrl = (filePath) => {
        if (!filePath) return null;
        if (filePath.startsWith('http')) return filePath;
        const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
        return `${baseURL}${filePath}`;
    };

    const formatDivisionSections = () => {
        // 편집 모드일 때는 editData 사용, 아닐 때는 organization 사용
        const sourceData = isEditMode ? editData : organization;
        const divisionSections = sourceData?.divisionSections;

        if (!divisionSections) return { div1: '0', div2: '0', div1Names: [], div2Names: [] };

        const divisions = typeof divisionSections === 'string'
            ? JSON.parse(divisionSections)
            : divisionSections;

        const div1 = Object.keys(divisions).length.toString();
        const div2 = Object.values(divisions).map(arr => Array.isArray(arr) ? arr.length : 0).join(',');

        // 소속기관1 이름들
        const div1Names = Object.keys(divisions);

        // 소속기관2 이름들 (모든 배열을 평탄화)
        const div2Names = Object.values(divisions)
            .filter(arr => Array.isArray(arr))
            .flat();

        return { div1, div2, div1Names, div2Names };
    };

    if (loading) {
        return (
            <ContentArea padding='20px 10px 10px 10px'>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                </Box>
            </ContentArea>
        );
    }

    if (error || !organization) {
        return (
            <ContentArea padding='20px 10px 10px 10px'>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <Typography color={theme.palette.figma.white[60]}>{error || t('organization_information_not_found')}</Typography>
                </Box>
            </ContentArea>
        );
    }

    const divisionInfo = formatDivisionSections();
    const orgType = ORGANIZATION_TYPES.find(t => t.value === (isEditMode ? editData.type : organization.type));
    const region = getRegionOptions(organization.country).find(r => r.value === (isEditMode ? editData.region : organization.region));
    const statusInfo = getStatusInfo(organization.state);

    return (
        <ContentArea padding='20px 10px 10px 10px'>
            <Box sx={{ px: 3, pb: 4, width: '100%', minWidth: 0, overflowX: 'auto' }}>
                {/* 헤더 - 우측 상단에 memo icon (승인대기, 재승인대기일 때만 표시) */}
                {(() => {
                    const stateNum = Number(organization?.state);
                    const PENDING = ORGANIZATION_STATE.find(s => s.value === 2)?.value;
                    const RE_PENDING = ORGANIZATION_STATE.find(s => s.value === 3)?.value;
                    const REJECTED = ORGANIZATION_STATE.find(s => s.value === 4)?.value;

                    const canShowMemo = (isSupervisor(meInfo?.grade) && (stateNum === PENDING || stateNum === RE_PENDING))
                        || (isAdmin(meInfo?.grade) && (stateNum === PENDING || stateNum === RE_PENDING || stateNum === REJECTED));
                    if (!isEditMode && canShowMemo) {
                        return (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                <IconButton
                                    onClick={handleMemoClick}
                                    sx={{
                                        color: theme.palette.figma.white[80],
                                        '&:hover': {
                                            color: theme.palette.figma.white[100],
                                        },
                                    }}
                                >
                                    <Memo />
                                </IconButton>
                            </Box>
                        );
                    }
                    return null;
                })()}
                <Grid container spacing={4} sx={{ mb: 4 }}>
                    {/* 왼쪽 컬럼 */}
                    <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                        {/* 기관 정보 테이블 */}
                        <CommonCard>
                            <TableContainer sx={{ overflowX: 'auto', width: '100%', minWidth: 0 }}>
                                <Table size="small" sx={{ width: '100%', minWidth: 'auto' }}>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('organization_name')}
                                            </TableCell>
                                            <TableCell>
                                                {isEditMode ? (
                                                    <InputField
                                                        value={editData.name}
                                                        onChange={(e) => handleEditDataChange('name', e.target.value)}
                                                        placeholder={t('enter_organization_name')}
                                                        size="small"
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: theme.palette.figma.white[10],
                                                                borderRadius: '15px',
                                                                height: '40px',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    organization.name || '-'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('organization_admin_account')}
                                            </TableCell>
                                            <TableCell>{adminUser?.loginId || '-'}</TableCell>
                                        </TableRow>
                                        {isEditMode && (
                                            <>
                                                <TableRow>
                                                    <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                        {t('account_password')}
                                                    </TableCell>
                                                    <TableCell>
                                                        <InputField
                                                            type="password"
                                                            showVisibilityIcon={true}
                                                            value={editData.password}
                                                            onChange={(e) => handleEditDataChange('password', e.target.value)}
                                                            placeholder={t('include_at_least_2_types_of_letters_numbers_and_symbols_6_12_characters')}
                                                            size="small"
                                                            fullWidth
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: theme.palette.figma.white[10],
                                                                    borderRadius: '15px',
                                                                    height: '40px',
                                                                },
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                        {t('account_password_confirmation')}
                                                    </TableCell>
                                                    <TableCell>
                                                        <InputField
                                                            type="password"
                                                            showVisibilityIcon={true}
                                                            value={editData.confirmPassword}
                                                            onChange={(e) => handleEditDataChange('confirmPassword', e.target.value)}
                                                            placeholder={t('include_at_least_2_types_of_letters_numbers_and_symbols_6_12_characters')}
                                                            size="small"
                                                            fullWidth
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: theme.palette.figma.white[10],
                                                                    borderRadius: '15px',
                                                                    height: '40px',
                                                                },
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            </>
                                        )}
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('organization_type')}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                    <span>{orgType ? toDisplayString(t(orgType.label), orgType.label) : '-'}</span>
                                                    {isEditMode ? (
                                                        <Button
                                                            size="small"
                                                            variant="normal"
                                                            onClick={handleViewOrganizationType}
                                                        >
                                                            {t('edit')}
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            size="small"
                                                            variant="normal"
                                                            onClick={handleViewOrganizationType}
                                                        >
                                                            {t('view')}
                                                        </Button>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('division_1')}
                                            </TableCell>
                                            <TableCell>
                                                {divisionInfo.div1}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('division_2')}
                                            </TableCell>
                                            <TableCell>
                                                {divisionInfo.div2}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CommonCard>

                        {/* 담당자 정보 테이블 */}
                        <CommonCard>
                            <TableContainer sx={{ overflowX: 'auto', width: '100%', minWidth: 0 }}>
                                <Table size="small" sx={{ width: '100%', minWidth: 'auto' }}>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('contact_name')}
                                            </TableCell>
                                            <TableCell>
                                                {isEditMode ? (
                                                    <InputField
                                                        value={editData.userName}
                                                        onChange={(e) => handleEditDataChange('userName', e.target.value)}
                                                        placeholder={t('enter_contact_name')}
                                                        size="small"
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: theme.palette.figma.white[10],
                                                                borderRadius: '15px',
                                                                height: '40px',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    adminUser?.name || organization.userName || '-'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('contact_title')}
                                            </TableCell>
                                            <TableCell>
                                                {isEditMode ? (
                                                    <InputField
                                                        value={editData.contactInfo}
                                                        onChange={(e) => handleEditDataChange('contactInfo', e.target.value)}
                                                        placeholder={t('enter_contact_title')}
                                                        size="small"
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: theme.palette.figma.white[10],
                                                                borderRadius: '15px',
                                                                height: '40px',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    organization.contactInfo || '-'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('contact_phone')}
                                            </TableCell>
                                            <TableCell>
                                                {isEditMode ? (
                                                    <InputField
                                                        value={editData.phone}
                                                        onChange={(e) => handleEditDataChange('phone', e.target.value)}
                                                        placeholder={t('enter_contact_phone')}
                                                        size="small"
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: theme.palette.figma.white[10],
                                                                borderRadius: '15px',
                                                                height: '40px',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    organization.phone || adminUser?.phone || '-'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('contact_email')}
                                            </TableCell>
                                            <TableCell>
                                                {isEditMode ? (
                                                    <InputField
                                                        value={editData.email}
                                                        onChange={(e) => handleEditDataChange('email', e.target.value)}
                                                        placeholder={t('enter_contact_email')}
                                                        size="small"
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: theme.palette.figma.white[10],
                                                                borderRadius: '15px',
                                                                height: '40px',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    adminUser?.email || organization.email || '-'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                첨부서류
                                            </TableCell>
                                            <TableCell>
                                                {isEditMode ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {editData.filePreview ? (
                                                            <>
                                                                <Link
                                                                    href={editData.file ? editData.filePreview : getFileUrl(editData.filePreview)}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    sx={{
                                                                        color: theme.palette.figma.white[80],
                                                                        textDecoration: 'underline',
                                                                        cursor: 'pointer',
                                                                        '&:hover': {
                                                                            color: theme.palette.figma.white[100],
                                                                        },
                                                                    }}
                                                                >
                                                                    {editData.file ? editData.file.name : 'file'}
                                                                </Link>
                                                            </>
                                                        ) : (
                                                            <Typography sx={{ color: theme.palette.figma.white[40], fontSize: '14px' }}>
                                                                파일 없음
                                                            </Typography>
                                                        )}
                                                        <input
                                                            type="file"
                                                            id="file-upload"
                                                            style={{ display: 'none' }}
                                                            onChange={handleFileChange}
                                                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                                                        />
                                                        <label htmlFor="file-upload">
                                                            <Button
                                                                component="span"
                                                                size="small"
                                                                variant="normal"
                                                                sx={{ cursor: 'pointer' }}
                                                            >
                                                                {editData.filePreview ? t('change_photo') : t('upload')}
                                                            </Button>
                                                        </label>
                                                    </Box>
                                                ) : (
                                                    organization.file ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Link
                                                                href={getFileUrl(organization.file)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                sx={{
                                                                    color: theme.palette.figma.white[80],
                                                                    textDecoration: 'underline',
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                        color: theme.palette.figma.white[100],
                                                                    },
                                                                }}
                                                            >
                                                                file
                                                            </Link>
                                                            <AttachFileIcon sx={{ fontSize: '16px', color: theme.palette.figma.white[80] }} />
                                                        </Box>
                                                    ) : (
                                                        '-'
                                                    )
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CommonCard>
                    </Grid>

                    {/* 오른쪽 컬럼 */}
                    <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, width: '100%' }}>
                        {/* 주소 정보 테이블 */}
                        <CommonCard>
                            <TableContainer sx={{ overflowX: 'auto', width: '100%', minWidth: 0 }}>
                                <Table size="small" sx={{ width: '100%', minWidth: 'auto' }}>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('country')}
                                            </TableCell>
                                            <TableCell>
                                                {isEditMode ? (
                                                    <Select
                                                        value={editData.country}
                                                        onChange={(e) => handleEditDataChange('country', e.target.value)}
                                                        size="small"
                                                        fullWidth
                                                        sx={{
                                                            backgroundColor: theme.palette.figma.white[10],
                                                            borderRadius: '15px',
                                                            height: '40px',
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                border: 'none',
                                                            },
                                                        }}
                                                    >
                                                        <MenuItem value="ko">{t('south_korea')}</MenuItem>
                                                    </Select>
                                                ) : (
                                                    organization.country === 'ko' ? t('south_korea') : organization.country || '-'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('zip_code')}
                                            </TableCell>
                                            <TableCell>
                                                {isEditMode ? (
                                                    <InputField
                                                        value={editData.zipcode}
                                                        onChange={(e) => handleEditDataChange('zipcode', e.target.value)}
                                                        placeholder={t('enter_zip_code')}
                                                        size="small"
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: theme.palette.figma.white[10],
                                                                borderRadius: '15px',
                                                                height: '40px',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    organization.zipcode || '-'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('address')}
                                            </TableCell>
                                            <TableCell>
                                                {isEditMode ? (
                                                    <InputField
                                                        value={editData.address}
                                                        onChange={(e) => handleEditDataChange('address', e.target.value)}
                                                        placeholder={t('enter_address')}
                                                        size="small"
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: theme.palette.figma.white[10],
                                                                borderRadius: '15px',
                                                                height: '40px',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    organization.address || '-'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('address_detail')}
                                            </TableCell>
                                            <TableCell>
                                                {isEditMode ? (
                                                    <InputField
                                                        value={editData.addressDetail}
                                                        onChange={(e) => handleEditDataChange('addressDetail', e.target.value)}
                                                        placeholder={t('enter_address_detail')}
                                                        size="small"
                                                        fullWidth
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                backgroundColor: theme.palette.figma.white[10],
                                                                borderRadius: '15px',
                                                                height: '40px',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    organization.addressDetail || '-'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('latitude_longitude')}
                                            </TableCell>
                                            <TableCell>
                                                {isEditMode ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                            <InputField
                                                                value={editData.latitude || ''}
                                                                onChange={(e) => handleEditDataChange('latitude', e.target.value)}
                                                                placeholder={t('enter_latitude')}
                                                                size="small"
                                                                sx={{
                                                                    flex: 1,
                                                                    maxWidth: 'calc(100% - 100px)',
                                                                    '& .MuiOutlinedInput-root': {
                                                                        backgroundColor: theme.palette.figma.white[10],
                                                                        borderRadius: '15px',
                                                                        height: '40px',
                                                                    },
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                            <InputField
                                                                value={editData.longitude || ''}
                                                                onChange={(e) => handleEditDataChange('longitude', e.target.value)}
                                                                placeholder={t('enter_longitude')}
                                                                size="small"
                                                                sx={{
                                                                    flex: 1,
                                                                    maxWidth: 'calc(100% - 100px)',
                                                                    '& .MuiOutlinedInput-root': {
                                                                        backgroundColor: theme.palette.figma.white[10],
                                                                        borderRadius: '15px',
                                                                        height: '40px',
                                                                    },
                                                                }}
                                                            />
                                                            <Button
                                                                size="small"
                                                                variant="normal"
                                                                onClick={handleViewMap}
                                                            >
                                                                {t('edit_details')}
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            <span>{t('latitude')}: {organization.latitude || '-'}</span>
                                                            <span>{t('longitude')}: {organization.longitude || '-'}</span>
                                                        </Box>
                                                        {organization.latitude && organization.longitude && (
                                                            <Button
                                                                size="small"
                                                                variant="normal"
                                                                onClick={handleViewMap}
                                                            >
                                                                {t('view')}
                                                            </Button>
                                                        )}
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                {t('region')}
                                            </TableCell>
                                            <TableCell>
                                                {isEditMode ? (
                                                    <Select
                                                        value={editData.region}
                                                        onChange={(e) => handleEditDataChange('region', e.target.value)}
                                                        size="small"
                                                        fullWidth
                                                        sx={{
                                                            backgroundColor: theme.palette.figma.white[10],
                                                            borderRadius: '15px',
                                                            height: '40px',
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                border: 'none',
                                                            },
                                                        }}
                                                    >
                                                        {getRegionOptions(organization.country).map((region) => (
                                                            <MenuItem key={region.value} value={region.value}>
                                                                {t(region.label)}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                ) : (
                                                    region ? t(region.label) : organization.region || '-'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CommonCard>

                        {/* 디딤 H/W 연결 섹션 (슈퍼바이저만 표시) */}
                        {isSupervisor(meInfo?.grade) && (
                            <>
                                {isEditMode && (
                                    <Typography sx={{
                                        mt: 2,
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        letterSpacing: '-0.42px',
                                        textAlign: 'left'
                                    }}>
                                        {t('didim_h_w_connection')}
                                    </Typography>
                                )}
                                <CommonCard sx={{ alignItems: 'stretch', gap: 3 }}>
                                    {isEditMode ? (
                                        <Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {/* 선택된 디딤H/W 목록 표시 */}
                                                {connectedDidims.map((item) => (
                                                    <Box key={item.id} sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '1fr auto',
                                                        alignItems: 'center',
                                                        backgroundColor: theme.palette.figma.white[5],
                                                        borderRadius: '8px',
                                                        padding: '8px 12px',
                                                        border: `1px solid ${theme.palette.figma.white[20]}`
                                                    }}>
                                                        <Typography sx={{
                                                            fontSize: '14px',
                                                            fontWeight: 500,
                                                            color: theme.palette.text?.primary || 'rgba(255, 255, 255, 0.9)',
                                                            textAlign: 'left'
                                                        }}>
                                                            {t('didim_id')} {item.id}
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemoveDidimHw(item.id)}
                                                            sx={{
                                                                padding: '4px',
                                                                color: theme.palette.figma.white[60],
                                                                '&:hover': {
                                                                    backgroundColor: theme.palette.figma.white[10],
                                                                    color: theme.palette.figma.Red_didim
                                                                }
                                                            }}
                                                        >
                                                            <Delete
                                                                size={20}
                                                                color={theme.palette.figma.icon}
                                                            />
                                                        </IconButton>
                                                    </Box>
                                                ))}

                                                {/* 해제된 디딤H/W 목록 표시 */}
                                                {removedDidimHwList.map((item) => (
                                                    <Box key={`removed-${item.id}`} sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '1fr auto',
                                                        alignItems: 'center',
                                                        backgroundColor: theme.palette.figma.white[5],
                                                        borderRadius: '8px',
                                                        padding: '8px 12px',
                                                        border: `1px solid ${theme.palette.figma.Red_didim}`,
                                                        opacity: 0.7
                                                    }}>
                                                        <Typography sx={{
                                                            fontSize: '14px',
                                                            fontWeight: 500,
                                                            color: theme.palette.figma.Red_didim,
                                                            textAlign: 'left'
                                                        }}>
                                                            {t('didim_id')} {item.id} ({t('to_be_removed')})
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                // 해제된 목록에서 제거하고 다시 선택된 목록에 추가
                                                                setRemovedDidimHwList(prev => prev.filter(removedItem => removedItem.id !== item.id));
                                                                setConnectedDidims(prev => [...prev, item]);
                                                            }}
                                                            sx={{
                                                                padding: '4px',
                                                                color: theme.palette.figma.Red_didim,
                                                                '&:hover': {
                                                                    backgroundColor: theme.palette.figma.white[10],
                                                                    color: theme.palette.figma.steps
                                                                }
                                                            }}
                                                        >
                                                            <Plus size={16} />
                                                        </IconButton>
                                                    </Box>
                                                ))}

                                                {/* 추가 버튼 */}
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: theme.palette.figma.white[5],
                                                    borderRadius: '8px',
                                                    padding: '8px 12px',
                                                    border: `1px solid ${theme.palette.figma.white[20]}`,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.figma.white[10]
                                                    }
                                                }}
                                                    onClick={handleOpenDidimHwDialog}
                                                >
                                                    <Plus size={20} color={theme.palette.figma.white[60]} />
                                                </Box>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <TableContainer sx={{ overflowX: 'auto', width: '100%', minWidth: 0 }}>
                                            <Table size="small" sx={{ width: '100%', minWidth: 'auto' }}>
                                                <TableBody>
                                                    <TableRow>
                                                        <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                            {t('didim_h_w_connection')}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box>
                                                                <span>{connectedDidims.length > 0 ? connectedDidims.map(d => d.id).join(', ') : '-'}</span>
                                                                <Typography variant="caption" sx={{ color: theme.palette.figma.white[60], fontSize: '12px', display: 'block', mt: 0.5 }}>
                                                                    ({t('can_add_after_approval')})
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </CommonCard>
                            </>
                        )}

                        {/* 상태 정보 테이블 (편집 모드일 때 숨김, 슈퍼바이저는 숨김, 어드민만 표시) */}
                        {((!isEditMode && isSupervisor(meInfo?.grade)) || isAdmin(meInfo?.grade)) && (
                            <CommonCard >
                                <TableContainer sx={{ overflowX: 'auto', width: '100%', minWidth: 0 }}>
                                    <Table size="small" sx={{ width: '100%', minWidth: 'auto' }}>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                    {t('status')}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                                        <Box sx={{ display: 'inline-block', overflow: 'visible' }}>
                                                            <StatusIndicator
                                                                text={statusInfo.text}
                                                                sx={{ backgroundColor: statusInfo.color }}
                                                                textColor="#000000"
                                                            />
                                                        </Box>
                                                        {(() => {
                                                            const stateNum = Number(organization?.state);
                                                            const RE_PENDING_STATE = ORGANIZATION_STATE.find(s => s.value === 3);
                                                            if (stateNum === RE_PENDING_STATE?.value && organization?.info) {
                                                                return (
                                                                    <Typography sx={{
                                                                        fontSize: '14px',
                                                                        color: theme.palette.figma.white[100] || theme.palette.figma.buttonText || '#ffffff',
                                                                        fontWeight: 400,
                                                                    }}>
                                                                        {organization.info}
                                                                    </Typography>
                                                                );
                                                            }
                                                            return null;
                                                        })()}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                    {t('application_date')}
                                                </TableCell>
                                                <TableCell>{organization.createDate ? formatDateTimeDisplay(organization.createDate, 'datetime') : '-'}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                    {t('status_update')}
                                                </TableCell>
                                                <TableCell>
                                                    {stateInfoDate && (
                                                        <>
                                                            {formatDateTimeDisplay(stateInfoDate, 'datetime')}
                                                            {stateInfoUserName && ` (${stateInfoUserName})`}
                                                        </>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            {(!isEditMode || isSupervisor(meInfo?.grade)) ? <TableRow>
                                                <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                    {t('reject_delete_memo')}
                                                </TableCell>
                                                <TableCell sx={{
                                                    overflow: 'hidden',
                                                    padding: '8px !important',
                                                    width: '82% !important',
                                                    maxWidth: '370px !important',
                                                    minWidth: 0
                                                }}>
                                                    {isSupervisor(meInfo?.grade) ? (
                                                        <InputField
                                                            value={stateMemo}
                                                            onChange={(e) => setStateMemo(e.target.value)}
                                                            placeholder={t('enter_content_for_the_organization_contact_when_rejecting_or_deleting')}
                                                            fullWidth
                                                            size="small"
                                                            sx={{
                                                                width: '100% !important',
                                                                maxWidth: '100% !important',
                                                                minWidth: 0,
                                                                boxSizing: 'border-box',
                                                                '& .MuiFormControl-root': {
                                                                    width: '100% !important',
                                                                    maxWidth: '100% !important',
                                                                    minWidth: 0,
                                                                },
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: theme.palette.figma.white[10],
                                                                    borderRadius: '15px',
                                                                    height: '40px',
                                                                    padding: '0 15px',
                                                                    maxWidth: '100% !important',
                                                                    width: '100% !important',
                                                                    minWidth: 0,
                                                                    boxSizing: 'border-box',
                                                                    '& fieldset': {
                                                                        border: 'none',
                                                                    },
                                                                    '& input': {
                                                                        color: theme.palette.text.main,
                                                                        fontSize: '14px',
                                                                        fontWeight: 500,
                                                                        letterSpacing: '-0.42px',
                                                                        padding: '0',
                                                                        maxWidth: '100% !important',
                                                                        width: '100% !important',
                                                                        minWidth: 0,
                                                                        boxSizing: 'border-box',
                                                                        '&::placeholder': {
                                                                            color: theme.palette.figma.white[40],
                                                                            opacity: 1,
                                                                        },
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                    ) : (
                                                        <Typography sx={{
                                                            fontSize: '14px',
                                                            fontWeight: 500,
                                                            color: theme.palette.text.main,
                                                            letterSpacing: '-0.42px',
                                                        }}>
                                                            {stateMemo || '-'}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                                :
                                                <TableRow>
                                                    <TableCell sx={{ bgcolor: theme.palette.figma.white[10], fontWeight: 600 }}>
                                                        {t('memo')}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        overflow: 'hidden',
                                                        padding: '8px !important',
                                                        width: '82% !important',
                                                        maxWidth: '370px !important',
                                                        minWidth: 0
                                                    }}>
                                                        <InputField
                                                            value={info}
                                                            onChange={(e) => setInfo(e.target.value)}
                                                            placeholder={t('please_enter_a_memo_regarding_the_edit')}
                                                            fullWidth
                                                            size="small"
                                                            sx={{
                                                                width: '100% !important',
                                                                maxWidth: '100% !important',
                                                                minWidth: 0,
                                                                boxSizing: 'border-box',
                                                                '& .MuiFormControl-root': {
                                                                    width: '100% !important',
                                                                    maxWidth: '100% !important',
                                                                    minWidth: 0,
                                                                },
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: theme.palette.figma.white[10],
                                                                    borderRadius: '15px',
                                                                    height: '40px',
                                                                    padding: '0 15px',
                                                                    maxWidth: '100% !important',
                                                                    width: '100% !important',
                                                                    minWidth: 0,
                                                                    boxSizing: 'border-box',
                                                                    '& fieldset': {
                                                                        border: 'none',
                                                                    },
                                                                    '& input': {
                                                                        color: theme.palette.text.main,
                                                                        fontSize: '14px',
                                                                        fontWeight: 500,
                                                                        letterSpacing: '-0.42px',
                                                                        padding: '0',
                                                                        maxWidth: '100% !important',
                                                                        width: '100% !important',
                                                                        minWidth: 0,
                                                                        boxSizing: 'border-box',
                                                                        '&::placeholder': {
                                                                            color: theme.palette.figma.Red_didim,
                                                                            opacity: 1,
                                                                        },
                                                                    },
                                                                },
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            }
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CommonCard>
                        )}
                    </Grid>
                </Grid>

                {/* 하단 액션 버튼 */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
                    {isEditMode ? (
                        // 편집 모드일 때: 취소, 저장하기 버튼
                        <>
                            <Button
                                variant="gray"
                                onClick={handleCancelEdit}
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                variant="normal"
                                onClick={handleSaveEdit}
                                disabled={isAdmin(meInfo?.grade) && (!info || info.trim() === '')}
                            >
                                {isAdmin(meInfo?.grade) ? t('save_organization_application') : t('save')}
                            </Button>
                        </>
                    ) : (
                        // 편집 모드가 아닐 때: 기존 버튼들 (슈퍼어드민만 표시)
                        isSupervisor(meInfo?.grade) && (() => {
                            const stateNum = Number(organization?.state);
                            const PENDING = ORGANIZATION_STATE.find(s => s.value === 2)?.value;
                            const RE_PENDING = ORGANIZATION_STATE.find(s => s.value === 3)?.value;
                            const REJECTED = ORGANIZATION_STATE.find(s => s.value === 4)?.value;
                            const DELETED = ORGANIZATION_STATE.find(s => s.value === 5)?.value;

                            // 반려 또는 삭제 상태인 경우
                            if (stateNum === REJECTED || stateNum === DELETED) {
                                return (
                                    <Button
                                        variant="normal"
                                        onClick={handleRestore}
                                    >
                                        {t('revert_to_pending')}
                                    </Button>
                                );
                            }
                            // 승인대기 또는 재승인대기 상태인 경우
                            else if (stateNum === PENDING || stateNum === RE_PENDING) {
                                return (
                                    <>
                                        <Button
                                            variant="gray"
                                            onClick={handleDelete}
                                        >
                                            {t('delete')}
                                        </Button>
                                        <Button
                                            variant="normal"
                                            onClick={handleReject}
                                        >
                                            {t('reject_with_email')}
                                        </Button>
                                        <Button
                                            variant="normal"
                                            onClick={handleApprove}
                                        >
                                            {t('approve_with_email')}
                                        </Button>
                                    </>
                                );
                            }
                            return null;
                        })()
                    )}
                </Box>
            </Box>

            {/* 기관유형 보기 팝업 */}
            <Dialog
                open={typeViewDialogOpen}
                onClose={handleCloseTypeViewDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        background: theme.palette.figma.popupBG,
                    },
                }}
            >
                <DialogContent sx={{ padding: '20px' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.figma.white[100] }}>
                        {t('org_type_and_division')}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ color: theme.palette.figma.white[80], mb: 1 }}>
                            {t('organization_type')}: {orgType ? t(orgType.label) : '-'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.figma.white[80], mb: 1 }}>
                            {t('division_1')}: {divisionInfo.div1Names && divisionInfo.div1Names.length > 0
                                ? divisionInfo.div1Names.join(', ')
                                : '-'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.figma.white[80] }}>
                            {t('division_2')}: {divisionInfo.div2Names && divisionInfo.div2Names.length > 0
                                ? divisionInfo.div2Names.join(', ')
                                : '-'}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: '20px', justifyContent: 'center' }}>
                    <Button
                        variant="normal"
                        onClick={handleCloseTypeViewDialog}
                        sx={{ minWidth: '120px' }}
                    >
                        {t('confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 지도 팝업 */}
            <MapDialog
                open={mapDialogOpen}
                onClose={handleCloseMapDialog}
                initialPosition={mapPosition}
                onSave={handleMapPositionSave}
            />

            {/* 확인 팝업 */}
            <Dialog
                open={confirmDialogOpen}
                onClose={handleCancelAction}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        background: theme.palette.figma.popupBG,
                    },
                }}
            >
                <DialogContent sx={{ padding: '20px' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.figma.white[100] }}>
                        {confirmMessage}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: '20px', justifyContent: 'center', gap: 2 }}>
                    <Button
                        variant="gray"
                        onClick={handleCancelAction}
                        sx={{ minWidth: '120px' }}
                    >
                        취소
                    </Button>
                    <Button
                        variant="normal"
                        onClick={handleConfirmAction}
                        sx={{ minWidth: '120px' }}
                    >
                        확인
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 디딤H/W 선택 팝업 */}
            <DidimHwDialog
                open={didimHwDialogOpen}
                onClose={handleCloseDidimHwDialog}
                onSelect={handleSelectDidimHw}
                excludeIds={connectedDidims.map(d => d.id)}
            />

            {/* 기관유형 편집 팝업 */}
            <OrganizationTypeEditDialog
                open={editDialogOpen}
                onClose={handleCloseEditDialog}
                onSave={handleSaveOrganizationType}
                initialType={isEditMode ? editData.type : organization?.type}
                initialDivisionSections={isEditMode ? editData.divisionSections : organization?.divisionSections}
                disabled={false}
            />

            {/* 일반 팝업 */}
            <Popup
                open={popupOpen}
                message={popupMessage}
                subMessage={popupSubMessage}
                onConfirm={handleClosePopup}
            />

            {/* 토스트 팝업 */}
            <Toast
                open={toastOpen}
                message={toastMessage}
                severity={toastSeverity}
                onClose={handleCloseToast}
            />
        </ContentArea>
    );
};

export default ApplicationDetail;
